import {
    RecordData,
    Form,
    FetchOptions,
    DatabaseResponse,
    GetRecordQuery,
    Condition,
    PostRecordConfig,
    ProgressCallback,
    BinaryFile,
    FileInfo,
    DelRecordQuery,
    Table,
    Index,
    Tag,
    UniqueId
} from '../Types';
import SkapiError from '../main/error';
import { extractFormData, fromBase62, isBrowserRuntime } from '../utils/utils';
import validator from '../utils/validator';
import { request, uploadFiles } from '../utils/network';
import { checkAdmin } from './user';
import { authentication } from './user';
import { accessGroup, indexValue, recordIdOrUniqueId, validateCustomIndexName, validateTableName, validateTag, validateStringByPolicy, indexRange } from './param_restrictions';

const pendingPrivateAccessKeyRequest: Record<string, Promise<string>> = {};

export async function normalizeRecord(record: Record<string, any>, _called_from?): Promise<RecordData> {
    // if (record?.rec) {
    //     if (_called_from !== 'called from postRecord') {
    //         let recPost = window.sessionStorage.getItem(`${this.service}:post:${record.rec}`);
    //         if (recPost) {
    //             try {
    //                 record = JSON.parse(recPost);
    //             }
    //             catch (err) { }
    //             window.sessionStorage.removeItem(`${this.service}:post:${record.rec}`);
    //         }
    //     }
    // }
    const output: Record<string, any> = {
        user_id: '',
        record_id: '',
        updated: 0,
        uploaded: 0,
        readonly: false,
        table: {
            name: '',
            access_group: 0,
            subscription: {
                is_subscription_record: false,
                upload_to_feed: false,
                notify_subscribers: false,
                feed_referencing_records: false,
                notify_referencing_records: false
            }
        },
        referenced_count: 0,
        source: {
            referencing_limit: null,
            prevent_multiple_referencing: false,
            can_remove_referencing_records: false,
            only_granted_can_reference: false,
        },
        ip: '',
        bin: {}
    };
    let is_anonymous = false;
    function access_group_set(v) {
        let access_group = v == '**' ? 'private' : parseInt(v);
        access_group = access_group == 0 ? 'public' : access_group == 1 ? 'authorized' : access_group == 99 ? 'admin' : access_group;
        return access_group;
    }
    const keys = {
        'ip': (r: string) => {
            let split_ip = r.split('#');
            let ip = split_ip[0];
            if (split_ip.length > 1) {
                output.unique_id = split_ip[1];
            }
            if (ip.slice(-1) === 'R') {
                output.readonly = true;
                ip = ip.slice(0, -1);
            }
            else {
                output.readonly = false;
            }
            // check if the format of the ip is 0-0-0-0, if it is, convert it to 0.0.0.0

            if (/^\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}$/.test(ip)) {
                ip = ip.split('-').join('.');
                is_anonymous = true;
            }

            output.ip = ip;
        },
        'rec': (r: string) => {
            if (!r) return;
            output.record_id = r;
            let base62timestamp = r.substring(0, r.length - 9); // id: [base62 timestamp][random 5 char][suid 4 char]
            let uploaded = fromBase62(base62timestamp);
            output.uploaded = uploaded;
        },
        'usr': (r: string) => {
            output.user_id = r;
        },
        'tbl': (r: string) => {
            if (!r) return;
            // table/service/group(** | group)/[subscription(user id)/group(00 - 99)]/[tag]
            if (!output.table.name) {
                let rSplit = r.split('/');
                output.table.name = rSplit[0];
                output.table.access_group = access_group_set(rSplit[2]);
                if (rSplit?.[3]) {
                    output.table.subscription.is_subscription_record = true;
                }
            }
        },
        'usr_tbl': (r: string) => {
            // user-id/table/service/group(** | group)[/subscription(user id)/group(00 - 99)][/tag]
            let rSplit = r.split('/');
            if (!output.user_id) {
                output.user_id = rSplit[0];
            }
            if (!output.table.name) {
                output.table.name = rSplit[1];
                output.table.access_group = access_group_set(rSplit[3]);
                if (rSplit?.[4]) {
                    output.table.subscription.is_subscription_record = true;
                }
            }
        },
        'idx': (r: string) => {
            if (!r) return;
            let rSplit = r.split('!');
            let name = rSplit.splice(0, 1)[0];
            let value = normalizeTypedString('!' + rSplit.join('!'));
            output.index = {
                name,
                value
            };
        },
        'ref': (r: string) => {
            if (!r) return;
            output.reference = r.split('/')[0];
        },
        'tags': (r: string[]) => {
            output.tags = r;
        },
        'upd': (r: number) => {
            output.updated = r;
        },
        'acpt_mrf': (r: boolean) => {
            output.source.prevent_multiple_referencing = !r;
        },
        'ref_limt': (r: number) => {
            output.source.referencing_limit = r;
        },
        'rfd': (r: number) => {
            output.referenced_count = r;
        },
        'bin': async (r: string[]) => {
            let binObj: Record<string, any[]> = {};
            let _ref = output?.reference || null;
            if (Array.isArray(r)) {
                const parsedBin = await Promise.all(r.map(async (url) => {
                    try {
                        let path = url.split('/').slice(3).join('/');
                        let splitPath = path.split('/');

                        // Expected path format:
                        // auth|publ/serviceid/ownerid/uploaderid/records/recordid/access_group/bin/timestamp/size/form_key/filename
                        if (splitPath.length < 12) {
                            return null;
                        }

                        let filename = decodeURIComponent(splitPath[splitPath.length - 1]);
                        let pathKey = decodeURIComponent(splitPath[10]);
                        let size = splitPath[9];
                        let uploaded = splitPath[8];
                        let access_group = access_group_set(splitPath[6]);
                        let url_endpoint = url;
                        if (access_group !== 'public') {
                            try {
                                url_endpoint = (await getFile.bind(this)(url, { dataType: 'endpoint', _ref }) as string);
                            }
                            catch (err) {
                                console.error('Error getting signed url for private file:', err);
                                // Keep the original CDN URL when signed endpoint resolution is unavailable.
                                url_endpoint = url;
                            }
                        }

                        let obj = {
                            access_group,
                            filename,
                            url: url_endpoint,
                            path,
                            size: fromBase62(size),
                            uploaded: fromBase62(uploaded),
                            getFile: (dataType: 'base64' | 'download' | 'endpoint' | 'blob' | 'text' | 'info', progress?: ProgressCallback) => {
                                let config = {
                                    dataType: dataType || 'download',
                                    progress,
                                    _ref,
                                    _update: obj
                                };
                                return getFile.bind(this)(url_endpoint, config);
                            }
                        };

                        return { pathKey, obj };
                    }
                    catch {
                        return null;
                    }
                }));

                for (let parsed of parsedBin) {
                    if (!parsed) {
                        continue;
                    }

                    if (binObj[parsed.pathKey]) {
                        binObj[parsed.pathKey].push(parsed.obj);
                        continue;
                    }

                    binObj[parsed.pathKey] = [parsed.obj];
                }
            }
            output.bin = binObj;
        },
        'prv_acs': (r: { [key: string]: string }) => {
            for (let k in r) {
                let subscription_config = ['notify_subscribers', 'upload_to_feed', 'feed_referencing_records', 'notify_referencing_records'];
                if (subscription_config.includes(k)) {
                    output.table.subscription[k] = r[k];
                }
                else {
                    output.source[k] = r[k];
                }
            }
        },
        'data': (r: any) => {
            let data = r;
            if (r === '!D%{}') {
                data = {};
            }
            else if (r === '!L%[]') {
                data = [];
            }
            output.data = data;
        }
    };

    if (record.record_id) {
        // bypass already normalized records
        return record as RecordData;
    }

    for (let k in keys) {
        if (record.hasOwnProperty(k)) {
            let exec = keys[k](record[k]);
            if (exec instanceof Promise) {
                await exec;
            }
        }
    }

    if (is_anonymous) {
        output.user_id = 'anonymous:' + output.user_id;
    }

    return output as RecordData;
}

function normalizeTypedString(v: string) {
    let value = v.substring(3);
    let type = v.substring(0, 3);

    switch (type) {
        case "!S%":
            // !S%string
            return value;
        case "!N%":
            // !N%0
            let splitDec = value.split('.');
            let calcNumb = Number(splitDec[0]) - 4503599627370496;
            if (splitDec.length === 1) {
                return calcNumb;
            }
            return parseFloat(calcNumb.toString() + '.' + splitDec[1]);
        case "!B%":
            // !B%1
            return value === '1';
        case "!L%":
        case "!D%":
            // !L%[0, "hello"] / !D%{}
            try {
                return JSON.parse(value);
            } catch (err) {
                throw new SkapiError('Value parse error.', { code: 'PARSE_ERROR' });
            }
        default:
            return v;
    }
}

export async function deleteFiles(params: {
    endpoints: string | string[], // file endpoints
}): Promise<RecordData[]> {
    let { endpoints } = params;

    if (typeof endpoints === 'string') {
        endpoints = [endpoints];
    }

    if (!Array.isArray(endpoints)) {
        throw new SkapiError('"endpoints" should be type: array | string.', { code: 'INVALID_PARAMETER' });
    }

    let updatedRec = await request.bind(this)('del-files', {
        endpoints,
        storage: 'records'
    }, { auth: true, method: 'post' });

    let to_process = [];
    for (let i in updatedRec) {
        to_process.push(normalizeRecord.bind(this)(updatedRec[i]));
    }

    return Promise.all(to_process);
}

export async function getFile(
    url: string, // cdn endpoint url https://xxxx.cloudfront.net/path/file
    config?: {
        dataType?: 'base64' | 'download' | 'endpoint' | 'blob' | 'text' | 'info'; // default 'download'
        expires?: number; // uses url that expires in given seconds. this option does not use the cdn (slow). can be used for private files. (does not work on public files).
        progress?: ProgressCallback;
        _ref?: string;
        _update?: any;
    }
): Promise<Blob | string | void | FileInfo> {
    if (typeof url !== 'string') {
        throw new SkapiError('"url" should be type: string.', { code: 'INVALID_PARAMETER' });
    }

    let splitQuery = url.split('?');
    let baseUrl = splitQuery.shift() || '';
    let queryString = splitQuery.length ? '?' + splitQuery.join('?') : '';

    let validatedBaseUrl = validator.Url(baseUrl);
    url = validatedBaseUrl + queryString;

    let isValidEndpoint = false;
    let splitUrl = validatedBaseUrl.split('/');
    let host = splitUrl[2];
    let splitHost = host.split('.');
    let subdomain = null;

    if (splitHost.length === 3 && splitHost[1] === 'skapi') {
        subdomain = splitHost[0];
        isValidEndpoint = true;
    }

    let target_key = splitUrl.slice(3);
    let needAuth = false;
    if (!isValidEndpoint) {
        if (target_key[0] === 'auth' || target_key[0] === 'publ') {
            try {
                validator.UserId(target_key[2]);
                validator.UserId(target_key[3]);
                needAuth = target_key[0] == 'auth';
                isValidEndpoint = true;
            }
            catch {
                throw new SkapiError('Invalid file url.', { code: 'INVALID_PARAMETER' });
            }
        }
    }

    let service = subdomain ? null : target_key[1];

    config = validator.Params<NonNullable<typeof config>>(config, {
        expires: ['number', () => 0],
        dataType: ['base64', 'blob', 'endpoint', 'text', 'info', () => 'download'],
        progress: 'function',
        _ref: [null, 'string'],
        _update: v => v
    });


    if (config?.dataType === 'info') {
        // auth(publ)/service-id/owner-id/user-id/records/rec-id/**/file(bin)/sizetag/filename
        return {
            url,
            filename: target_key[target_key.length - 1],
            fileKey: target_key[target_key.length - 2],
            access_group: target_key[6] === '**' ? 'private' : target_key[6] === '01' ? 'authorized' : target_key[6] === '00' ? 'public' : parseInt(target_key[6]),
            uploader: target_key[3],
            record_id: target_key[4] === 'records' ? target_key[5] : 'N/A',
            filesize: fromBase62(target_key[9]),
            uploaded: fromBase62(target_key[8]),
        }
    }

    let filename = url.split('/').slice(-1)[0];

    // if ((config?.dataType === 'blob' || config?.dataType === 'base64') && needAuth) {
    //     // when downloading blob, use signed url
    //     config.expires = 60;
    // }

    let expires = config.expires;
    if (expires) {
        if (!isValidEndpoint) {
            throw new SkapiError('Expires option can only be used on skapi cdn endpoints.', { code: 'INVALID_PARAMETER' });
        }

        if (expires < 0) {
            throw new SkapiError('"config.expires" should be > 0. (seconds)', { code: 'INVALID_PARAMETER' });
        }

        let params: Record<string, any> = {
            request: subdomain ? 'get-host' : 'get',
            id: subdomain || target_key[5],
            key: url,
            expires
        }

        if (service) {
            params.service = service
        }

        url = (await request.bind(this)('get-signed-url', params,
            { auth: true }
        )).url;
    }

    else if (needAuth) {
        let currTime = Math.floor(Date.now() / 1000);

        if (!this.bearerToken && (!this.session?.idToken?.payload?.exp || this.session.idToken.payload.exp < currTime)) {
            this.log('getFile:requesting new token', null);
            try {
                await authentication.bind(this)().getSession({ refreshToken: true });
                this.log('getFile:received new tokens', {
                    exp: this.session?.idToken?.payload?.exp,
                    currTime,
                    expiresIn: this.session?.idToken?.payload?.exp - currTime,
                    token: this.session?.accessToken?.jwtToken,
                    refreshToken: this.session?.refreshToken?.token
                });
            }
            catch (err) {
                this.log('getFile:new token error', err);
                throw new SkapiError('User login is required.', { code: 'INVALID_REQUEST' });
            }
        }

        let token = this.bearerToken || this.session?.idToken?.jwtToken; // idToken

        if (!token || !this.user?.user_id) {
            throw new SkapiError('User login is required.', { code: 'INVALID_REQUEST' });
        }

        url += `${url.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}`;

        let access_group = target_key[6] === '**' ? '**' : parseInt(target_key[6]);
        let user_access_group = this.user?.access_group ?? -1;

        if (this.user.user_id !== target_key[3] && (access_group === '**' || user_access_group < access_group)) {
            let record_id = target_key[5];
            if (this.__private_access_key[record_id] && typeof this.__private_access_key[record_id] === 'string') {
                url += `&p=${encodeURIComponent(this.__private_access_key[record_id])}`;
            }
            else if (this.owner !== this.host) {
                try {
                    let p = await this.requestPrivateRecordAccessKey({ record_id, reference_id: config?._ref });
                    url += `&p=${encodeURIComponent(p)}`;
                } catch (err) { }
            }
        }
    }

    if (config?.dataType === 'endpoint') {
        if (config._update) {
            // updates the url in the record (when called from the record bin object)
            config._update.url = url;
        }
        return url;
    }

    if (config?.dataType === 'download') {
        let a = document.createElement('a');
        // Set the href attribute to the file URL
        a.href = url;
        document.body.appendChild(a);
        a.setAttribute('download', filename);
        a.target = '_blank';
        a.click();
        document.body.removeChild(a);
        return null;
    }

    let blob: Promise<Blob | string> = new Promise(async (res, rej) => {
        try {
            let b = await request.bind(this)(
                url,
                null,
                { method: 'get', contentType: null, responseType: config?.dataType === 'text' ? 'text' : 'blob', fetchOptions: { progress: config?.progress } },
                { ignoreService: true }
            );
            if (config?.dataType === 'base64') {
                const reader = new FileReader();
                reader.onloadend = () => res((reader.result as string));
                reader.readAsDataURL(b);
            }
            else {
                res(b);
            }
        } catch (err) {
            rej(err);
        }
    });

    return blob;
}

async function getQuery(query, isDel = false) {
    query = extractFormData(query, { ignoreEmpty: true }).data || {};

    let is_reference_fetch = '';
    let rec_or_uniq = recordIdOrUniqueId(query);

    if (rec_or_uniq) {
        query = rec_or_uniq;
        is_reference_fetch = query.record_id || query.unique_id;

        if (typeof is_reference_fetch === 'string') {
            if (typeof this.__private_access_key?.[is_reference_fetch] === 'string') {
                query.private_key = this.__private_access_key?.[is_reference_fetch] || undefined;
            }
            if (this.__my_unique_ids[is_reference_fetch]) {
                if (isDel) {
                    delete this.__my_unique_ids[is_reference_fetch];
                }
                else {
                    query.record_id = this.__my_unique_ids[is_reference_fetch];
                    // delete query.unique_id;
                }
            }
        }
    }
    else {
        let isAdmin = await checkAdmin.bind(this)();
        let ref: any = query?.reference;
        let ref_user_is_me = false;

        if (typeof ref === 'object' && Object.keys(ref).length) {
            if (ref?.record_id || ref?.unique_id) {
                // if (ref.unique_id && this.__my_unique_ids[ref.unique_id]) {
                //     ref.record_id = this.__my_unique_ids[ref.unique_id];
                //     delete ref.unique_id;
                // }

                is_reference_fetch = ref.record_id || ref.unique_id;

                if (is_reference_fetch && typeof this.__private_access_key?.[is_reference_fetch] === 'string') {
                    query.private_key = this.__private_access_key?.[is_reference_fetch] || undefined;
                }

                query.reference = is_reference_fetch;
            }
            else if (ref?.user_id) {
                ref_user_is_me = ref.user_id === this.user?.user_id;
                query.reference = ref.user_id;
            }
        }

        if (typeof query?.table === 'string') {
            query.table = {
                name: query.table,
                access_group: 0
            };
        }

        if (query.index) {
            if (query.index.hasOwnProperty('range') && query.index.hasOwnProperty('condition')) {
                delete query.index.range;
            }
        }

        const buildStruct = (query) => {
            return {
                table: {
                    name: v => validateTableName(v, 'table.name'),
                    access_group: accessGroup.bind(this),
                    subscription: (v: any) => validator.UserId(v, 'User ID in "subscription"')
                },
                reference: 'string',
                index: {
                    name: ['$updated', '$uploaded', '$referenced_count', '$user_id', (v: string) => {
                        return validateCustomIndexName(v, 'index.name');
                    }],
                    value: (v: number | boolean | string) => {
                        const indexTypes = {
                            '$updated': 'number',
                            '$uploaded': 'number',
                            '$referenced_count': 'number',
                            '$user_id': validator.UserId
                        };

                        if (indexTypes.hasOwnProperty(query.index.name)) {
                            let tp = indexTypes[query.index.name];

                            if (typeof tp === 'function') {
                                return tp(v);
                            }

                            if (tp !== typeof v) {
                                throw new SkapiError(`"index.value" should be type: ${tp}.`, { code: 'INVALID_PARAMETER' });
                            }

                            return v;
                        }

                        if (typeof v === 'string' && !v) {
                            return "";
                        }

                        return indexValue(v);
                    },
                    condition: ['gt', 'gte', 'lt', 'lte', '>', '>=', '<', '<=', '=', 'eq'],
                    range: (v: number | boolean | string) => indexRange(v, query)
                },
                tag: (v: string) => {
                    if (v === null || v === undefined) {
                        return v;
                    }
                    if (typeof v === 'string') {
                        return validateTag(v, 'tag');
                    }
                    else {
                        throw new SkapiError('"tag" should be type: string.', { code: 'INVALID_PARAMETER' });
                    }
                },
                private_key: 'string'
            }
        }

        query = validator.Params(query || {}, buildStruct(query), ref_user_is_me || isAdmin ? [] : ['table'], { ignoreEmpty: true });
    }
    return {
        query,
        is_reference_fetch
    }
}

export async function getRecords(query: GetRecordQuery & { private_key?: string; }, fetchOptions?: FetchOptions): Promise<DatabaseResponse<RecordData>> {
    await this.__connection;

    let q = await getQuery.bind(this)(query);
    let is_reference_fetch = q.is_reference_fetch;

    // TODO: THINK ABOUT HOW TO HANDLE PRIVATE KEY FOR REFERENCE FETCH.
    // if (is_reference_fetch && typeof this.__private_access_key[is_reference_fetch] === 'string') {
    //     q.query.private_key = this.__private_access_key[is_reference_fetch] || undefined;
    // }

    let result = await request.bind(this)(
        'get-records',
        q.query,
        {
            fetchOptions,
            auth: !!this.__user,
            method: !!this.__user ? 'post' : 'get'
        }
    );

    if (is_reference_fetch && result?.reference_private_key && typeof result.reference_private_key === 'string') {
        this.__private_access_key[is_reference_fetch] = result.reference_private_key;
    }

    let to_process = [];
    for (let i in result.list) {
        to_process.push(normalizeRecord.bind(this)(result.list[i]));
    }

    result.list = await Promise.all(to_process);
    return result;
}

function setupPostRecordConfig(config: PostRecordConfig & { data?: any; }) {
    let is_reference_post = "";
    let files = [];
    let _config = validator.Params(config || {}, {
        record_id: (v) => validateStringByPolicy(v, 'record_id', {
            allowEmpty: false,
            onlyAlphanumeric: true,
        }),
        unique_id: ['string', null],
        readonly: 'boolean',
        table: {
            name: v => validateTableName(v, 'table.name'),
            subscription: [null, {
                is_subscription_record: 'boolean',
                upload_to_feed: 'boolean',
                notify_subscribers: 'boolean',
                feed_referencing_records: 'boolean',
                notify_referencing_records: 'boolean',
            }],
            access_group: accessGroup.bind(this),
        },
        source: {
            referencing_limit: (v: number) => {
                if (v === null) {
                    return null;
                }

                else if (typeof v === 'number') {
                    if (0 > v) {
                        throw new SkapiError(`"reference_limit" should be >= 0`, { code: 'INVALID_PARAMETER' });
                    }

                    if (v > 4503599627370546) {
                        throw new SkapiError(`"reference_limit" should be <= 4503599627370546`, { code: 'INVALID_PARAMETER' });
                    }

                    return v;
                }

                throw new SkapiError(`"reference_limit" should be type: <number | null>`, { code: 'INVALID_PARAMETER' });
            },
            prevent_multiple_referencing: 'boolean',
            can_remove_referencing_records: 'boolean',
            only_granted_can_reference: 'boolean',
            allow_granted_to_grant_others: 'boolean',
            referencing_index_restrictions: v => {
                if (v === undefined) {
                    return undefined;
                }

                if (!v) {
                    return null;
                }

                if (Array.isArray(v) && !v.length) {
                    return null;
                }

                let p = {
                    name: v => validateCustomIndexName(v, '"name" in "index_restrictions"'),
                    value: v => indexValue(v),
                    condition: ['gt', 'gte', 'lt', 'lte', '>', '>=', '<', '<=', '=', 'eq', '!=', 'ne', () => null],
                    range: val => {
                        if (val !== null && typeof v.value !== typeof val) {
                            throw new SkapiError('Index restriction "range" type should match the type of "value".', { code: 'INVALID_PARAMETER' });
                        }
                        if (!v.hasOwnProperty('value')) {
                            throw new SkapiError('Index restriction "value" is required.', { code: 'INVALID_PARAMETER' });
                        }
                        return val;
                    }
                }

                if (!Array.isArray(v)) {
                    v = [v];
                }

                let qq = v.map(vv => validator.Params(vv, p, ['name']));
                if (qq.length) {
                    for (let q of qq) {
                        if (q.condition && q.hasOwnProperty('range')) {
                            delete q.range;
                        }
                    }
                }
                return qq;
            },
        },
        reference: v => {
            if (v === null) {
                return { record_id: null };
            }
            if (!v) {
                return undefined;
            }
            if (typeof v === 'string') {
                is_reference_post = v;
                if (this.__my_unique_ids[v]) {
                    return this.__my_unique_ids[v];
                }
                return v;
            }

            if (typeof v !== 'object') {
                throw new SkapiError('"reference" should be type: <string | object>.', { code: 'INVALID_PARAMETER' });
            }

            return validator.Params(v, {
                unique_id: 'string',
                record_id: v => {
                    if (v === null || v === undefined) {
                        return v;
                    }
                    is_reference_post = v;
                    if (typeof this.__private_access_key?.[v] === 'string') {
                        config.reference_private_key = this.__private_access_key[v] || undefined;
                    }
                    return validateStringByPolicy(v, 'reference.record_id', {
                        allowEmpty: false,
                        onlyAlphanumeric: true,
                    });
                }
            });
        },
        index: [null, {
            name: v => validateCustomIndexName(v, 'index.name'),
            value: v => indexValue(v)
        }],
        tags: (v: string | string[]) => {
            if (v === null || v === undefined) {
                return v;
            }
            if (typeof v === 'string') {
                v = v.split(',').map(t => t.trim());
            }
            if (!Array.isArray(v)) {
                throw new SkapiError('"tag" should be type: <string | string[]>.', { code: 'INVALID_PARAMETER' });
            }
            return v.map(t => validateTag(t, 'tag'));
        },
        remove_bin: (v: string[] | BinaryFile[] | null) => {
            if (!v) {
                return null;
            }

            let arr = []
            if (Array.isArray(v)) {
                for (let i of v) {
                    if (typeof i === 'string') {
                        arr.push(decodeURIComponent(i.split('?')[0]));
                    }
                    else if (i.url && i.size && i.filename) {
                        let hostUrl = i.url.split('/').slice(0, 3).join('/');
                        let url = hostUrl + '/' + i.path;
                        arr.push(url);
                    }
                    else {
                        throw new SkapiError(`"remove_bin" should be type: <string[] | BinaryFile[] | null>`, { code: 'INVALID_PARAMETER' });
                    }
                }
            }
            else {
                throw new SkapiError(`"remove_bin" should be type: <string[] | BinaryFile[] | null>`, { code: 'INVALID_PARAMETER' });
            }

            return arr;
        },
        progress: 'function',
        data: v => v
    }, [], {
        precall: (pc) => {
            const data = pc?.data || {};

            if (!data?.record_id && !data.table) {
                throw new SkapiError('"table.name" is required.', { code: 'INVALID_PARAMETER' });
            }

            if (typeof data.table === 'string') {
                data.table = {
                    name: data.table,
                    access_group: 0
                };
            }

            if (pc.files) {
                files = pc.files;
            }
        }
    });

    let progress = config.progress || null;

    // callbacks should be removed after cocochex
    delete _config.progress;

    if (!this.__user) {
        const hasEnabledSubscriptionOptions = !!(
            _config.table?.subscription?.is_subscription_record
            || _config.table?.subscription?.upload_to_feed
            || _config.table?.subscription?.notify_subscribers
            || _config.table?.subscription?.feed_referencing_records
            || _config.table?.subscription?.notify_referencing_records
        );

        if (_config.record_id) {
            throw new SkapiError('Public users cannot update existing records.', { code: 'INVALID_REQUEST' });
        }
        if (_config.table.access_group !== 'public' && _config.table.access_group !== 0) {
            throw new SkapiError('Public users can only post records to public tables.', { code: 'INVALID_REQUEST' });
        }
        if (hasEnabledSubscriptionOptions) {
            throw new SkapiError('Public users cannot post subscription records.', { code: 'INVALID_REQUEST' });
        }
        if (_config.remove_bin) {
            throw new SkapiError('Public users cannot remove files from records.', { code: 'INVALID_REQUEST' });
        }
        if (_config.unique_id) {
            throw new SkapiError('Public users cannot set unique_id for records.', { code: 'INVALID_REQUEST' });
        }
    }
    return { config: _config, progress, is_reference_post, files };
}

export async function bulkPostRecords(params) {
    await this.__connection;

    if (!Array.isArray(params) || !params.length) {
        throw new SkapiError('"params" should be a non-empty array.', { code: 'INVALID_PARAMETER' });
    }

    let is_public = !this.__user;
    let reference_posts = [];
    let service = undefined;
    let owner = undefined;
    let progress = null;

    let validatedBulk = params.map((config, idx) => {
        if (!config || typeof config !== 'object' || Array.isArray(config)) {
            throw new SkapiError(`"params[${idx}]" should be type: <object>.`, { code: 'INVALID_PARAMETER' });
        }

        let mangled = setupPostRecordConfig.bind(this)(config) as {config: PostRecordConfig & { service?: string; owner?: string;  }; is_reference_post?: string;};
        let _config = mangled.config;
        if (mangled.is_reference_post) {
            reference_posts.push(mangled.is_reference_post);
        }
        if (typeof _config.progress === 'function' && progress === null) {
            progress = _config.progress;
        }

        if (_config.service !== undefined) {
            if (service === undefined) {
                service = _config.service;
            }
            else if (service !== _config.service) {
                throw new SkapiError('All bulk params should share the same "service" value.', { code: 'INVALID_PARAMETER' });
            }
        }

        if (_config.owner !== undefined) {
            if (owner === undefined) {
                owner = _config.owner;
            }
            else if (owner !== _config.owner) {
                throw new SkapiError('All bulk params should share the same "owner" value.', { code: 'INVALID_PARAMETER' });
            }
        }

        delete _config.progress;
        delete _config.service;
        delete _config.owner;

        return _config;
    });

    let postData = {
        _is_bulk_: validatedBulk,
        service: "",
        owner: ""
    };

    if (service !== undefined) {
        postData.service = service;
    }

    if (owner !== undefined) {
        postData.owner = owner;
    }

    let options = { auth: !!this.__user, method: 'post' };
    let fetchOptions = {};

    // if (typeof progress === 'function') {
    //     fetchOptions.progress = progress;
    // }

    if (Object.keys(fetchOptions).length) {
        Object.assign(options, { fetchOptions });
    }

    let recList = await request.bind(this)('post-record', postData, options);
    let records = await Promise.all(recList.map((rec) => normalizeRecord.bind(this)(rec, 'called from postRecord')));

    for (let i = 0; i < recList.length; i++) {
        let rec = recList[i];
        let record = records[i];

        // if (rec?.rec) {
        //     window.sessionStorage.setItem(`${this.service}:post:${rec.rec}`, JSON.stringify(rec));
        // }

        if (typeof rec?.reference_private_key === 'string') {
            for (let ref of reference_posts) {
                this.__private_access_key[ref] = rec.reference_private_key;
            }
        }

        if (record?.unique_id) {
            this.__my_unique_ids[record.unique_id] = record.record_id;
        }
    }

    // if (Object.keys(this.__my_unique_ids).length) {
    //     window.sessionStorage.setItem(`${this.service}:uniqueids`, JSON.stringify(this.__my_unique_ids));
    // }

    return records;
}

export async function postRecord(
    form: Form<Record<string, any>> | null | undefined,
    config: PostRecordConfig,
    files?: { name: string, file: File }[],
): Promise<RecordData> {
    await this.__connection;

    if (!config) {
        throw new SkapiError('"config" argument is required.', { code: 'INVALID_PARAMETER' });
    }

    let postConf = setupPostRecordConfig.bind(this)(config);
    let _config = postConf.config;
    let progress = postConf.progress;
    let is_reference_post = postConf.is_reference_post;

    let options: { [key: string]: any } = { auth: !!this.__user, method: 'post' };

    let to_bin: { name: string, file: File }[] = [];
    let extractedForm = extractFormData(form);
    if (files && Array.isArray(files) && files.length) {
        // { name: string, file: File }[]
        to_bin = to_bin.concat(files);
    }
    if (extractedForm.files && Array.isArray(extractedForm.files) && extractedForm.files.length) {
        // { name: string, file: File }[]
        to_bin = to_bin.concat(extractedForm.files);
    }

    let postData = null;
    postData = Object.assign({ data: extractedForm.data }, _config);

    let fetchOptions: { [key: string]: any } = {};

    if (typeof progress === 'function') {
        fetchOptions.progress = progress;
    }

    if (Object.keys(fetchOptions).length) {
        Object.assign(options, { fetchOptions });
    }
    let rec = await request.bind(this)('post-record', postData, options);
    if (isBrowserRuntime() && to_bin.length) {
        let bin_formData = new FormData();
        for (let f of to_bin) {
            bin_formData.append(f.name, f.file, f.file.name);
        }
        let uploadFileParams = {
            record_id: rec.rec,
            progress
        }
        if (_config.hasOwnProperty('service')) {
            uploadFileParams['service'] = _config.service;
        }
        let { bin_endpoints } = await uploadFiles.bind(this)(bin_formData, uploadFileParams);
        if (!rec.bin) {
            rec.bin = bin_endpoints;
        }
        else {
            rec.bin.push(...bin_endpoints);
        }
    }

    if (is_reference_post && typeof rec?.reference_private_key === 'string') {
        this.__private_access_key[is_reference_post] = rec.reference_private_key;
    }

    // window.sessionStorage.setItem(`${this.service}:post:${rec.rec}`, JSON.stringify(rec));

    let record = await normalizeRecord.bind(this)(rec, 'called from postRecord');
    if (record.unique_id) {
        this.__my_unique_ids[record.unique_id] = record.record_id;
        if (isBrowserRuntime()) {
            window.sessionStorage.setItem(`${this.service}:uniqueids`, JSON.stringify(this.__my_unique_ids));
        }
    }

    return record;
}

export async function getTables(
    /** If null fetch all list of tables. */
    query: {
        table: string;
        /** Condition operator of table name. */
        condition?: Condition;
    },
    fetchOptions?: FetchOptions
): Promise<DatabaseResponse<Table>> {
    let res = await request.bind(this)('get-table', validator.Params(query || {}, {
        table: 'string',
        condition: ['gt', 'gte', 'lt', 'lte', '>', '>=', '<', '<=', '=', 'eq', '!=', 'ne']
    }), Object.assign({ auth: !!this.__user }, { fetchOptions }));

    let convert = {
        'cnt_rec': 'number_of_records',
        'tbl': 'table',
        'srvc': 'service'
    };

    if (Array.isArray(res?.list)) {
        for (let t of res.list) {
            for (let k in convert) {
                if (t.hasOwnProperty(k)) {
                    t[convert[k]] = t[k];
                    delete t[k];
                }
            }
        }
    }

    return res;
}

export async function getIndexes(
    query: {
        /** Table name */
        table: string;
        /** Index name. When period is at the end of name, querys nested index keys. */
        index?: string;
        /** Queries order by */
        order?: {
            /** Key name to order by. */
            by: 'average_number' | 'total_number' | 'number_count' | 'average_bool' | 'total_bool' | 'bool_count' | 'string_count' | 'index_name';
            /** Value to query. */
            value?: number | boolean | string;
            condition?: Condition;
        };
    },
    fetchOptions?: FetchOptions
): Promise<DatabaseResponse<Index>> {
    if (!query?.table) {
        throw new SkapiError('"table" is required.', { code: 'INVALID_PARAMETER' });
    }

    let p:any = validator.Params(
        query || {},
        {
            table: 'string',
            index: (v: string) => validateCustomIndexName(v, 'index.name'),
            order: {
                by: [
                    'average_number',
                    'total_number',
                    'number_count',
                    'average_bool',
                    'total_bool',
                    'bool_count',
                    'string_count',
                    'index_name',
                    'number_of_records'
                ],
                value: ['string', 'number', 'boolean'],
                condition: ['gt', 'gte', 'lt', 'lte', '>', '>=', '<', '<=', '=', 'eq', '!=', 'ne']
            }
        },
        ['table']
    );

    if (p.hasOwnProperty('order')) {
        if (!p.order?.by) {
            throw new SkapiError('"order.by" is required.', { code: 'INVALID_PARAMETER' });
        }

        if (p.order.hasOwnProperty('condition') && !p.order.hasOwnProperty('value')) {
            throw new SkapiError('"value" is required for "condition".', { code: 'INVALID_PARAMETER' });
        }

        if (p.hasOwnProperty('index')) {
            if (p.index.substring(p.index.length - 1) !== '.') {
                throw new SkapiError('"index" should be a parent index name of the compound index when using "order.by"', { code: 'INVALID_PARAMETER' });
            }
        }
    }

    let res = await request.bind(this)(
        'get-index',
        p,
        Object.assign(
            { auth: !!this.__user },
            { fetchOptions }
        )
    );

    let convert = {
        'cnt_bool': 'boolean_count',
        'cnt_numb': 'number_count',
        'totl_numb': 'total_number',
        'totl_bool': 'total_bool',
        'avrg_numb': 'average_number',
        'avrg_bool': 'average_bool',
        'cnt_str': 'string_count'
    };

    if (Array.isArray(res?.list)) {
        res.list = res.list.map((i: Record<string, any>) => {
            let iSplit = i.idx.split('/');
            let resolved: Record<string, any> = {
                table: iSplit[1],
                index: iSplit[2],
                number_of_records: i.cnt_rec
            };

            for (let k in convert) {
                if (Object.prototype.hasOwnProperty.call(i, k)) {
                    resolved[convert[k]] = i[k];
                }
            }

            return resolved;
        });
    }

    return res;
}

export async function getTags(
    query?: {
        /** Table name */
        table: string;
        /** Tag name */
        tag?: string;
        /** String query condition for tag name. */
        condition?: Condition;
    },
    fetchOptions?: FetchOptions
): Promise<DatabaseResponse<Tag>> {

    let res = await request.bind(this)(
        'get-tag',
        validator.Params(query || {},
            {
                table: 'string',
                tag: 'string',
                condition: ['gt', 'gte', 'lt', 'lte', '>', '>=', '<', '<=', '=', 'eq', '!=', 'ne']
            }
        ),
        Object.assign({ auth: !!this.__user }, { fetchOptions })
    );

    if (Array.isArray(res?.list)) {
        res.list = res.list.map(item => {
            let tSplit = item.tag.split('/');
            return {
                table: tSplit[1],
                tag: tSplit[0],
                number_of_records: item.cnt_rec
            };
        });
    }

    return res;
}

export async function getUniqueId(
    query?: Form<{
        /** Unique ID */
        unique_id?: string;
        /** String query condition for tag name. */
        condition?: Condition;
    }>,
    fetchOptions?: FetchOptions
): Promise<DatabaseResponse<UniqueId>> {

    let res = await request.bind(this)(
        'get-uniqueid',
        validator.Params(query || {},
            {
                unique_id: 'string',
                condition: ['gt', 'gte', 'lt', 'lte', '>', '>=', '<', '<=', '=', 'eq', '!=', 'ne']
            }
        ),
        Object.assign({ auth: !!this.__user }, { fetchOptions })
    );

    if (Array.isArray(res?.list)) {
        for (let i in res.list) {
            let item = res.list[i];
            res.list[i] = {
                unique_id: item.unq,
                record_id: item.rec
            };
        }
    }

    return res;
}

export async function deleteRecords(query: DelRecordQuery & { private_key?: string; }, fetchOptions?: FetchOptions): Promise<string | DatabaseResponse<RecordData>> {
    await this.__connection;

    let q = await getQuery.bind(this)(query, true);
    let is_reference_fetch = q.is_reference_fetch;
    let result = await request.bind(this)('del-records', q.query, { auth: true, fetchOptions });
    if (is_reference_fetch && typeof result?.reference_private_key === 'string') {
        this.__private_access_key[is_reference_fetch] = result.reference_private_key;
    }

    return result?.message || result;
}

export function grantPrivateRecordAccess(params: {
    record_id: string;
    user_id: string | string[];
}) {
    return recordAccess.bind(this)({
        record_id: params.record_id,
        user_id: params.user_id,
        execute: 'add'
    });
}

export function removePrivateRecordAccess(params: {
    record_id: string;
    user_id: string | string[];
}) {
    return recordAccess.bind(this)({
        record_id: params.record_id,
        user_id: params.user_id || null,
        execute: 'remove'
    });
}

export async function listPrivateRecordAccess(p: {
    record_id?: string;
    user_id?: string | string[];
}, fetchOptions?: FetchOptions): Promise<DatabaseResponse<{ record_id: string; user_id: string; }>> {
    let params = {
        record_id: p.record_id || undefined,
        user_id: p.user_id || undefined,
        execute: 'list'
    };

    if (!params.record_id && !params.user_id) {
        throw new SkapiError(`Either record_id or user_id must be provided.`, { code: 'INVALID_PARAMETER' });
    }

    if (params.user_id) {
        if (typeof params.user_id === 'string') {
            validator.UserId(params.user_id);
            params.user_id = [params.user_id];
        }
        else if (Array.isArray(params.user_id)) {
            for (let u of params.user_id) {
                validator.UserId(u);
            }
        }
        else {
            throw new SkapiError(`user_id should be type: <string | string[]>`, { code: 'INVALID_PARAMETER' });
        }
    }

    let mapper = (i: Record<string, any>) => {
        if (i.rec_usr) {
            i.record_id = i.rec_usr.split('/')[0];
            i.user_id = i.rec_usr.split('/')[1];
        }
        else if (i.usr_rec) {
            i.user_id = i.usr_rec.split('/')[0];
            i.record_id = i.usr_rec.split('/')[1];
        }
        return i;
    };

    let list = await request.bind(this)(
        'grant-private-access',
        params,
        { auth: true, fetchOptions }
    );

    list.list = list.list.map(mapper);

    return list;
}

export function requestPrivateRecordAccessKey(params: { record_id: string, reference_id?: string }): Promise<string> {
    let record_id: string | string[] = params.record_id;
    let reference_id = params.reference_id || undefined;
    if (!record_id) {
        throw new SkapiError(`Record ID is required.`, { code: 'INVALID_PARAMETER' });
    }

    if (typeof record_id !== 'string') {
        throw new SkapiError(`Record ID should be type: <string | string[]>`, { code: 'INVALID_PARAMETER' });
    }

    if (reference_id && typeof reference_id !== 'string') {
        throw new SkapiError(`Reference ID should be type: <string>`, { code: 'INVALID_PARAMETER' });
    }

    if (typeof this.__private_access_key[record_id] === 'string') {
        return Promise.resolve(this.__private_access_key[record_id]);
    }

    if (pendingPrivateAccessKeyRequest[record_id]) {
        return pendingPrivateAccessKeyRequest[record_id];
    }

    let res = request
        .bind(this)(
            'request-private-access-key',
            { record_id, reference_id },
            { auth: true }
        )
        .then((r: any) => {
            let privateKey = typeof r === 'string' ? r : r?.private_key;
            if (typeof privateKey !== 'string') {
                throw new SkapiError('Invalid private access key response.', { code: 'ERROR' });
            }

            this.__private_access_key[record_id] = privateKey;
            return privateKey;
        })
        .finally(() => {
            delete pendingPrivateAccessKeyRequest[record_id];
        });

    pendingPrivateAccessKeyRequest[record_id] = res;

    return res;
}

function recordAccess(params: {
    record_id: string;
    user_id: string | string[];
    execute: 'add' | 'remove';
}): Promise<any> {
    let execute = params.execute;
    let req = validator.Params(params,
        {
            record_id: 'string',
            user_id: (v: string | string[]) => {
                if (!v) {
                    throw new SkapiError(`User ID is required.`, { code: 'INVALID_PARAMETER' });
                }

                let id = v;
                if (typeof id === 'string') {
                    id = [id];
                }

                if (id.length > 100) {
                    throw new SkapiError(`Cannot process more than 100 users at once.`, { code: 'INVALID_REQUEST' });
                }

                for (let i of id) {
                    validator.UserId(i, 'User ID in "user_id"');
                }

                return id;
            },
            execute: ['add', 'remove']
        },
        [
            'execute',
            'record_id',
            'user_id'
        ]
    );

    if (!req.user_id) {
        req.user_id = null;
    }

    return request.bind(this)(
        'grant-private-access',
        req,
        { auth: true }
    );
}

